"use client";
import React, { useState, useEffect } from "react";
// import Home from "../../components/HomeScreen";
// import Layout from "../../components/Layout";
import dynamic from "next/dynamic";
import { Tour, Button } from 'antd';
import type { TourProps, GetRef } from 'antd';
import { TourContext } from "@/pages/_app";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { useDispatch } from "react-redux";
import { setSessionId } from "@/redux/reducers/chatSlice";
import WhatsNewModal from "../../components/WhatsNewModal";

const Layout = dynamic(() => import("../../components/Layout"), { ssr: false });
const ChatLayout = dynamic(() => import("../../components/ChatLayout"), {
  ssr: false,
});
const Home = dynamic(() => import("../../components/HomeScreen"), {
  ssr: false,
});

interface Props {
  // define any props needed for the component
}


const HomeContainer: React.FC<Props> = () => {
  const [newChat, setNewChat] = useState(true);
  // const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hideSearchButtons, setHideSearchButtons] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const router = useRouter();
  const dispatch = useDispatch();
  const { data: session, status } = useSession();
  const prevStatusRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!router.isReady) return;
    const path = router.asPath.split("?")[0] || "";
    if (path.startsWith("/home/") && path !== "/home") {
      const routeSessionIdRaw = router.query?.sessionId;
      const routeSessionId =
        Array.isArray(routeSessionIdRaw)
          ? (routeSessionIdRaw[0] ?? "").trim()
          : typeof routeSessionIdRaw === "string"
            ? routeSessionIdRaw.trim()
            : "";
      if (routeSessionId) {
        dispatch(setSessionId(routeSessionId));
      }
      setNewChat(false);
      return;
    }
    if (path === "/home" || path === "/home/") {
      dispatch(setSessionId(""));
      setNewChat(true);
    }
  }, [router.isReady, router.asPath, router.query?.sessionId, dispatch]);
  const hasShownModalRef = React.useRef<boolean>(false);
  const lastUserEmailRef = React.useRef<string | null>(null);

const {documentSearchTourRef,peopleSearchTourRef,tredenceDisTourRef,uploadDocTourRef,termsMarNoteTourRef, sdlcAgentsTourRef,spacesTourRef,openTour,setOpenTour,newChatTourRef,libraryTourRef,isLibraryModalVisible } = React.useContext(TourContext)!;
const [currentStep, setCurrentStep] = useState(0);
const [isSpaceIconVisible, setIsSpaceIconVisible] = useState(false);

  // Helper function to determine if we're on the home screen
  const isOnHomeScreen = React.useMemo(() => {
    if (!router.isReady) return false;
    
    const asPath = router.asPath;
    // Home screen is exactly '/home' or '/'
    // Exclude chat history routes (/home/[sessionId]) and SDLC agents page
    if (asPath === '/agent-workflow') return false;
    if (asPath.startsWith('/home/') && asPath !== '/home') return false; // Chat history
    return asPath === '/home' || asPath === '/';
  }, [router.asPath, router.isReady]);

  // Check if space icon exists
  useEffect(() => {
    if (!router.isReady) return;
    
    const checkSpaceIcon = () => {
      const iconExists = spacesTourRef.current !== null && spacesTourRef.current !== undefined;
      setIsSpaceIconVisible(iconExists);
    };
    
    // Check immediately
    checkSpaceIcon();
    
    // Check after a small delay to account for dynamic rendering
    const timeoutId = setTimeout(checkSpaceIcon, 200);
    
    // Set up a polling interval to check periodically (every 500ms)
    // This ensures we catch the icon even if it's rendered later
    const intervalId = setInterval(checkSpaceIcon, 500);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [spacesTourRef, router.isReady]);

  // Hide tour when not on home screen (chat history, SDLC agents, or library modal is open)
  useEffect(() => {
    if (!router.isReady) return;
    
    // Hide tour if:
    // 1. Not on home screen
    // 2. Library modal is open
    if ((!isOnHomeScreen || isLibraryModalVisible) && openTour) {
      setOpenTour(false);
      setCurrentStep(0);
    }
  }, [isOnHomeScreen, isLibraryModalVisible, openTour, router.isReady, setOpenTour]);

  // Show "What's New" modal on every logout/login
  useEffect(() => {
    // Track status transitions: show modal when transitioning to authenticated
    if (status === "authenticated" && session?.user?.email) {
      // Store the current user email for later cleanup
      lastUserEmailRef.current = session.user.email;
      
      // Only show modal if we're transitioning FROM unauthenticated/loading TO authenticated
      // If previous status was already "authenticated", this is just navigation (like New Chat click)
      const wasUnauthenticated = prevStatusRef.current === "unauthenticated" || 
                                  prevStatusRef.current === null ||
                                  prevStatusRef.current === "loading";
      
      // Check sessionStorage to prevent showing modal on component remounts (like New Chat navigation)
      const storageKey = `whatsNewModalShown_${session.user.email}`;
      const hasShownInStorage = typeof window !== 'undefined' && sessionStorage.getItem(storageKey) === 'true';
      
      // Show modal only if:
      // 1. This is a fresh login (status transition from unauthenticated/loading to authenticated)
      // 2. Previous status was NOT already authenticated (to prevent showing on navigation)
      // 3. We haven't shown it yet in this component instance
      // 4. We haven't shown it in sessionStorage (prevents showing on component remounts)
      if (wasUnauthenticated && 
          prevStatusRef.current !== "authenticated" && 
          !hasShownModalRef.current && 
          !hasShownInStorage) {
        // Small delay to ensure page is loaded
        setTimeout(() => {
          setShowWhatsNewModal(true);
          hasShownModalRef.current = true;
          // Mark as shown in sessionStorage to prevent showing on remounts
          if (typeof window !== 'undefined') {
            sessionStorage.setItem(storageKey, 'true');
          }
        }, 500);
      }
    } else if (status === "unauthenticated") {
      // Reset flags when user logs out so modal will show again on next login
      hasShownModalRef.current = false;
      
      // Clear sessionStorage entry when user logs out (use stored email from ref)
      if (typeof window !== 'undefined' && lastUserEmailRef.current) {
        const storageKey = `whatsNewModalShown_${lastUserEmailRef.current}`;
        sessionStorage.removeItem(storageKey);
        // Also clear all entries that start with whatsNewModalShown_ as a fallback
        try {
          const keysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('whatsNewModalShown_')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => sessionStorage.removeItem(key));
        } catch (e) {
          // Ignore errors in case sessionStorage is not available
        }
      }
      // Clear the stored email
      lastUserEmailRef.current = null;
    }
    
    // Update previous status
    prevStatusRef.current = status;
  }, [status, session]);

  const handleCloseWhatsNewModal = () => {
    setShowWhatsNewModal(false);
    // Mark as shown in sessionStorage when user closes the modal
    if (session?.user?.email && typeof window !== 'undefined') {
      const storageKey = `whatsNewModalShown_${session.user.email}`;
      sessionStorage.setItem(storageKey, 'true');
    }
  };

  const handleOkWhatsNewModal = () => {
    setShowWhatsNewModal(false);
    // Mark as shown in sessionStorage when user clicks OK
    if (session?.user?.email && typeof window !== 'undefined') {
      const storageKey = `whatsNewModalShown_${session.user.email}`;
      sessionStorage.setItem(storageKey, 'true');
    }
  };
  const allSteps: TourProps['steps'] = [
    {
      title: 'New chat',
      description: 'Start a new conversation to explore a new topic from scratch.',
      target: () => newChatTourRef.current!,
      placement: "rightTop"
    },
    {
      title: 'Library',
      description: 'View all your uploaded files and collections, and add your query-ready collection to the chat',
      placement: 'rightTop',
      target: () => libraryTourRef.current!,
    },
    {
      title: 'SDLC Agents',
      description: 'Easily create SDLC documents such as RFP, TTD and BRDs Agent responses by uploading your project requirements with the help of dedicated Agents.',
      placement: 'rightTop',
      target: () => sdlcAgentsTourRef.current!,
    },
    {
      title: 'Teams Meeting Notes',
      description: 'Select meetings from here to get thier  detailed summaries.',
      placement: "rightTop",
      target: () => termsMarNoteTourRef.current!,
    },
    {
      title: 'Spaces',
      description: 'Welcome to your Account Space – a centralized hub to manage onboarding tasks and track readiness for the account.',
      placement: "rightTop",
      target: () => spacesTourRef.current!,
    },
    {
      title: 'Upload Documents',
      description: 'Use this button to upload files and create a ready collection. You can access all previously uploaded documents in the Library section.',
      target: () => uploadDocTourRef.current!,
      placement: "rightTop"
    },
    // {
    //   title: 'Tredence Discover',
    //   description: 'Access information from the Tredence knowledge base, including bussiness solutions, company policies, and accelerator guidelines  .',
    //   target: () => tredenceDisTourRef.current!,
    //   placement: "bottomLeft"
    // },
    // {
    //   title: 'People Search',
    //   description: 'This is your People Assistant. Get a clear view of people, their roles, skills and billing status',
    //   target: () => peopleSearchTourRef.current!,
    //   placement: "bottomLeft"
    // },
    // {
    //   title: 'Document Search',
    //   description: 'Access your Documents seamlessly from your Sharepoint Sites.',
    //   target: () => documentSearchTourRef.current!,
    //   placement: "bottomLeft"
    // },
  ];

  // Filter steps to only include spaces step if the icon is visible
  const steps = React.useMemo(() => {
    return allSteps.filter((step) => {
      // Include spaces step only if icon is visible
      if (step.title === 'Spaces') {
        return isSpaceIconVisible;
      }
      return true;
    });
  }, [isSpaceIconVisible]);

  return (
 <>
      <Layout>
        <ChatLayout
          newChat={newChat}
          setNewChat={setNewChat}
          hideSearchButtons={hideSearchButtons}
          setHideSearchButtons={setHideSearchButtons}
        >
          <Home
            newChat={newChat}
            setNewChat={setNewChat}
            hideSearchButtons={hideSearchButtons}
            setHideSearchButtons={setHideSearchButtons}
          />
        </ChatLayout>
      </Layout>
      <Tour
        gap={{ offset: [1, 4], radius: 6 }}
        open={openTour && isOnHomeScreen && !isLibraryModalVisible}
        current={currentStep} // Bind the current step to your state
        onClose={() => {
          setOpenTour(false);
          setCurrentStep(0);
        }}
        steps={steps}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
        actionsRender={(_, { current, total }) => (
          <>
            {current > 0 && (
              <Button size="small" onClick={() => {
                setCurrentStep(currentStep - 1); // Go to previous step
              }}>
                Previous
              </Button>
            )}
            <Button
              size="small"
              style={{
                backgroundColor: "#FE5000", // Solid button color
                color: "#FFFFFF",            // White text
                border: "none",              // Remove border
                boxShadow: "none",           // Optional: remove shadow
                cursor: "pointer",           // Ensure pointer cursor
                padding: "0 7px",           // Optional padding
                // height: 3,                  // Optional height
              }}
              onClick={() => {
                if (current === total - 1) {
                  setOpenTour(false); // Done
                  setCurrentStep(0);
                } else {
                  setCurrentStep(currentStep + 1); // Go to next step
                }
              }}
            >
              {current === total - 1 ? "Done" : "Next"}
            </Button>
          </>
        )}
      />
      <WhatsNewModal
        visible={showWhatsNewModal}
        onClose={handleCloseWhatsNewModal}
        onOk={handleOkWhatsNewModal}
      />
    </>
  );
};

export default HomeContainer;
